import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from 'components/common';
import { FormGroup, Text } from 'components/forms';
import { payments } from 'libs';
import type { PaymentFormSubmitData } from './types';

interface PaymentFormProps {
  buttonLabel: string;
  isSubmitting: boolean;
  onSubmit: (data: PaymentFormSubmitData, clear: () => void) => void | Promise<void>;
}

const options = {
  hidePostalCode: true,
  style: {
    base: {
      color: '#32325d',
      lineHeight: '24px',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const schema = yup.object({
  name: yup.string().required('The name field is required.'),
  pm_postal: yup.string().required('The postal code field is required.'),
});

const PaymentForm = ({ buttonLabel, isSubmitting, onSubmit }: PaymentFormProps) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [isCardReady, setIsCardReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const [setupIntentToken, setSetupIntentToken] = useState<string>();
  const [cardElement] = useState(stripe.elements().create('card', options));
  const cardElementContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Clears all form fields.
   */
  const clear = () => {
    reset();
    cardElement.clear();
    setCardError('');
  };

  /**
   * Handle the submission to stripe before passing to onSubmit handler.
   * @param {Object} event
   */
  const handleStripe = async (data: PaymentFormSubmitData) => {
    const { setupIntent, error } = await stripe.confirmCardSetup(setupIntentToken as string, {
      payment_method: {
        card: cardElement,
        billing_details: {
          address: {
            postal_code: data.pm_postal,
          },
        },
      },
    });
    if (setupIntent) {
      return onSubmit({ ...data, setupIntent }, clear);
    }
    if (error!.type === 'invalid_request_error') {
      toast.error('Credit card details not updated. To keep your information secure, refresh the page and try again.');
    }
    return null;
  };

  /**
   * Display the stripe errors on the card.
   * @param {Object} options.error
   */
  const handleStripeError = ({ error }: StripeCardChangeEvent) => {
    setCardError(error?.message || '');
  };

  useEffect(() => {
    if (!cardElementContainerRef.current) {
      return undefined;
    }

    const handleCardReady = () => setIsCardReady(true);

    payments.getSetupIntentToken().then(setSetupIntentToken);
    cardElement.mount(cardElementContainerRef.current);
    cardElement.addEventListener('change', handleStripeError);
    cardElement.addEventListener('ready', handleCardReady);

    return () => {
      cardElement.removeEventListener('change', handleStripeError);
      cardElement.removeEventListener('ready', handleCardReady);
      cardElement.destroy();
    };
  }, [cardElement]);

  return (
    <form
      data-is-ready={isCardReady && Boolean(setupIntentToken)}
      data-testid="billing-payment-card-form"
      onSubmit={handleSubmit(handleStripe)}
    >
      <Text
        error={errors.name}
        label="Cardholder's name *"
        name="name"
        register={register('name')}
        testId="billing-payment-card-form-name-field"
        type="text"
      />
      <Text
        error={errors.pm_postal}
        label="Postal code *"
        name="pm_postal"
        register={register('pm_postal')}
        testId="billing-payment-card-form-postal-code-field"
        type="text"
      />
      {/* Stripe Elements must be mounted in a DOM element that
      can contain child nodes. */}
      <FormGroup>
        <div className="fw-bold mb-2">Card number *</div>
        <div>
          <div className="form-control" ref={cardElementContainerRef} />
          <span className="text-red text-0_875" data-testid="billing-payment-card-form-errors">
            {cardError}
          </span>
        </div>
      </FormGroup>
      <FormGroup>
        <div>
          <Button
            disabled={isSubmitting}
            submit
            testId="billing-payment-card-form-submit-button"
            text="bold"
            variant="primary-dark"
          >
            {buttonLabel}
          </Button>
        </div>
      </FormGroup>
    </form>
  );
};

export default PaymentForm;
